<template>
  <div
    id="creatives-preview"
    class="d-flex flex-column overflow-hidden"
  >
    <header
      id="creatives-preview-header"
      class="bg-secondary d-flex align-items-center justify-content-center"
      style="height: 58px;"
    >
      <div
        class="d-flex align-items-center"
      >
        <b-button
          variant="tertiary"
          class="d-flex align-items-center"
          @click="updateCarouselIndex(-1)"
          :disabled="!carouselPrevButtonEnabled"
        >
          <i class="h5 mb-0 fal fa-chevron-left" />
        </b-button>
        <h5
          id="creative-template-name"
          class="mb-0"
        >
          <strong>
            {{ GET_ACTIVE_CREATIVE_TEMPLATES[carouselIndex].name }}
          </strong>
        </h5>
        <b-button
          variant="tertiary"
          class="d-flex align-items-center"
          data-test="next-creative-btn"
          @click="updateCarouselIndex(+1)"
          :disabled="!carouselNextButtonEnabled"
        >
          <i class="h5 mb-0 fal fa-chevron-right" />
        </b-button>
      </div>
    </header>
    <div
      id="carousel-container"
      ref="carousel-container"
      class="w-100 flex-grow-1 p-5 overflow-hidden"
      @click="setIsSidebarOpen(isAddingNewAnnotation)"
    >
      <ul
        id="carousel"
        ref="carousel-slider"
        class="list-unstyled d-flex m-0 p-0 h-100 align-items-center"
      >
        <li
          v-for="(creativeTemplate, index) in GET_ACTIVE_CREATIVE_TEMPLATES"
          :key="creativeTemplate.id"
          ref="creatives-carousel-slides"
          class="carousel__slide h-100 w-100 flex-shrink-0 d-flex align-items-center justify-content-center"
        >
          <div
            v-if="isIFrameLoading"
            class="loader"
          />
          <div
            v-show="!isIFrameLoading && selectedArticleUuid"
            :ref="`iframe-container-${creativeTemplate.creativeId}`"
            class="iframe-container"
            :style="{ width: getCreativeTemplateWidth(index), height: getCreativeTemplateHeight(index) }"
          >
            <content-annotations
              v-if="allowAnnotations"
              :style="{ width: getCreativeTemplateWidth(index), height: getCreativeTemplateHeight(index) }"
            />
            <iframe
              :key="selectedCreativeArticleCombo"
              :ref="`preview-iframe-${creativeTemplate.creativeId}`"
              :id="`preview-iframe-${creativeTemplate.creativeId}`"
              class=""
              title="Content Preview"
              :srcdoc="getSourceDocument(creativeTemplate)"
              :width="getCreativeTemplateWidth(index)"
              :height="getCreativeTemplateHeight(index)"
              scrolling="no"
              marginwidth="0"
              marginheight="0"
              hspace="0"
              vspace="0"
              frameborder="0"
              @load="handleIFrameLoad"
            />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import * as contentTypes from '@/modules/content/store/types';

import ContentAnnotations from '@/modules/content/components/ContentAnnotations';

export default {
  name: 'CreativesPreview',
  components: {
    ContentAnnotations,
  },
  props: {
    isSidebarOpen: {
      type: Boolean,
      required: true,
      default: false,
    },
  },
  data() {
    return {
      resizeObserver: null,
      carouselContainerWidth: 0,
      carouselContainerHeight: 0,
      carouselIndex: 0,
      isIFrameLoading: false,
      areInteractionsTimedOut: false,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
    ]),
    ...mapState('content', [
      'isSidebarInputFocused',
      'timestamp',
      'creativeTemplates',
      'creativesSizes',
      'selectedArticleUuid',
      'selectedCreativeId',
      'allowAnnotations',
      'isAddingNewAnnotation',
    ]),
    ...mapGetters('content', [
      contentTypes.GET_ARTICLE_BY_UUID,
      contentTypes.GET_ACTIVE_CREATIVE_TEMPLATES,
    ]),
    selectedCreativeTemplate() {
      return this.GET_ACTIVE_CREATIVE_TEMPLATES[this.carouselIndex];
    },
    selectedCreativeArticleCombo() {
      return `${this.selectedCreativeId}-${this.selectedArticleUuid}`;
    },
    carouselPrevButtonEnabled() {
      return !this.areInteractionsTimedOut
      && this.carouselIndex > 0;
    },
    carouselNextButtonEnabled() {
      return !this.areInteractionsTimedOut
      && this.carouselIndex < this.GET_ACTIVE_CREATIVE_TEMPLATES.length - 1;
    },
    slides() {
      return this.$refs['creatives-carousel-slides'];
    },
    selectedArticle() {
      return this.GET_ARTICLE_BY_UUID(this.selectedArticleUuid) || {};
    },
    isKeyboardNavigationBlocked() {
      return this.isSidebarInputFocused;
    },
  },
  methods: {
    ...mapActions('content', [
      contentTypes.SET_SELECTED_CREATIVE_ID,
    ]),
    getSourceDocument(creativeTemplate) {
      return `\x3cscript src="${process.env.VUE_APP_RENDER_API}/preview/creative/${creativeTemplate.creativeId}/key/${creativeTemplate.creativeKey}/acid/${this.selectedArticleUuid}?${this.timestamp}"\x3e\x3c/script\x3e`;
    },
    updateCarouselIndex(step) {
      if (this.areInteractionsTimedOut) return;
      if (step === -1 && !this.carouselPrevButtonEnabled) return;
      if (step === +1 && !this.carouselNextButtonEnabled) return;
      const slides = this.$refs['creatives-carousel-slides'];

      const outgoingslide = slides[this.carouselIndex];
      outgoingslide.classList.add('fadeOut');

      const incomingSlide = slides[this.carouselIndex + step];
      incomingSlide.classList.add('fadeIn');

      outgoingslide.addEventListener('animationend', () => {
        outgoingslide.classList.remove('current-slide');
        outgoingslide.classList.remove('fadeOut');
      }, { once: true });

      this.carouselIndex += step;

      incomingSlide.addEventListener('animationend', () => {
        incomingSlide.classList.add('current-slide');
        incomingSlide.classList.remove('fadeIn');
      }, { once: true });
      this.SET_SELECTED_CREATIVE_ID(this.selectedCreativeTemplate.creativeId);
      this.setCreativeTemplateScaling();
      slides.forEach((slide) => {
        slide.style.transform = `translateX(${this.carouselIndex * -this.carouselContainerWidth}px)`;
      });

      this.areInteractionsTimedOut = true;
      setTimeout(() => {
        this.areInteractionsTimedOut = false;
      }, 100);
    },
    setCreativeTemplateScaling() {
      const iFrameContainer = this.$refs[`iframe-container-${this.selectedCreativeId}`][0];
      let scaleAmount = 1;
      if (this.carouselContainerWidth < this.selectedCreativeTemplate.width) {
        scaleAmount = this.carouselContainerWidth / this.selectedCreativeTemplate.width;
        iFrameContainer.style.setProperty('transform-origin', 'center center');
      } else if (this.carouselContainerHeight < this.selectedCreativeTemplate.height) {
        scaleAmount = this.carouselContainerHeight / this.selectedCreativeTemplate.height;
        iFrameContainer.style.setProperty('transform-origin', 'center center');
      } else iFrameContainer.style.setProperty('transform-origin', 'center center');
      iFrameContainer.style.setProperty('transform', `scale(${scaleAmount})`);
    },
    handleIFrameLoad() {
      if (!this.selectedArticleUuid) return;
      this.isIFrameLoading = true;
      const iFrameDocument = document.getElementById(`preview-iframe-${this.selectedCreativeId}`).contentWindow.document;
      this.setCreativeTemplateScaling();
      this.SET_SELECTED_CREATIVE_ID(this.selectedCreativeTemplate.creativeId);
      if (iFrameDocument?.querySelector('body')?.hasChildNodes()) this.isIFrameLoading = false;
    },
    getCreativeTemplateWidth(index) {
      return `${this.GET_ACTIVE_CREATIVE_TEMPLATES[index].width}px`;
    },
    getCreativeTemplateHeight(index) {
      return `${this.GET_ACTIVE_CREATIVE_TEMPLATES[index].height}px`;
    },
    updateRoute(creativeKey) {
      const { campaignId } = this.$route.params;
      const { selectedItem } = this.$route.query;
      this.$router
        .replace({
          name: 'Campaign Showcase',
          params: { campaignId },
          query: { creativeKey, selectedItem },
        });
    },
    handleKeydown(event) {
      if (this.isKeyboardNavigationBlocked) return;
      if (event.key === 'ArrowRight') {
        this.updateCarouselIndex(+1);
        this.updateRoute(this.selectedCreativeTemplate.creativeKey);
        return;
      }
      if (event.key === 'ArrowLeft') {
        this.updateCarouselIndex(-1);
        this.updateRoute(this.selectedCreativeTemplate.creativeKey);
      }
    },
    async attachResizeObserver() {
      this.detachResizeObserver();
      this.resizeObserver = new ResizeObserver(() => {
        const rootElement = document.querySelector(':root');

        const carouselContainerComputedStyles = window.getComputedStyle(this.$refs['carousel-container']);
        const paddingX = parseInt(carouselContainerComputedStyles['padding-left'], 10) + parseInt(carouselContainerComputedStyles['padding-right'], 10);
        this.carouselContainerWidth = this.$refs['carousel-container'].clientWidth - paddingX;
        const paddingY = parseInt(carouselContainerComputedStyles['padding-top'], 10) + parseInt(carouselContainerComputedStyles['padding-bottom'], 10);
        this.carouselContainerHeight = this.$refs['carousel-container'].clientHeight - paddingY;

        this.setCreativeTemplateScaling();
        this.slides.forEach((slide) => {
          slide.style.setProperty('transform', `translateX(${this.carouselIndex * -this.carouselContainerWidth}px)`);
        });
        rootElement.style.setProperty('--carousel-container-width', `${this.$refs['carousel-container'].clientWidth}px`);
      });
      this.resizeObserver.observe(this.$refs['carousel-container']);
    },
    detachResizeObserver() {
      if (this.resizeObserver instanceof ResizeObserver) {
        this.resizeObserver.unobserve(this.$refs['carousel-container']);
      }
    },
    setIsSidebarOpen(isOpen) {
      this.$emit('setIsSidebarOpen', !!isOpen);
    },
  },
  async mounted() {
    this.isIFrameLoading = true;
    window.addEventListener('keydown', this.handleKeydown);
    const { creativeKey } = this.$route.query;
    if (creativeKey) {
      this.carouselIndex = this.GET_ACTIVE_CREATIVE_TEMPLATES.findIndex((creativeTemplate) => creativeTemplate.creativeKey === creativeKey) || 0;
    }
    await this.attachResizeObserver();
    await this.$refs['creatives-carousel-slides'][this.carouselIndex].classList.add('current-slide');
  },
  beforeDestroy() {
    window.removeEventListener('keydown', this.handleKeydown);
    this.detachResizeObserver();
  },
};
</script>
<style scoped lang="scss">
#creative-template-name {
  min-width: 240px;
  text-align: center;
}
#carousel-container {
  width: var(--carousel-container-width, auto);
  #carousel {
    width: inherit;
    .carousel__slide {
      width: inherit;
      opacity: 0;
      pointer-events: none;
    }
    .current-slide {
      opacity: 1;
      pointer-events: visible;
    }
    .fadeOut {
      animation-name: fadeOut;
      animation-duration: .5s;
      animation-fill-mode: forwards;
      transition: transform .5s ease-in-out;
    }
    .fadeIn {
      animation-name: fadeIn;
      animation-duration: .5s;
      animation-fill-mode: forwards;
      transition: transform .5s ease-in-out;
    }
  }
}
@keyframes fadeOut {
  0% {
    opacity: 1;
  }
  40% {
    opacity: 0;
  }
  100% {
    opacity: 0;
  }
}
@keyframes fadeIn {
  0% {
    opacity: 0;
  }
  60% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
.iframe-container {
  position: relative;
}
</style>
